import {
	BusyIndicator,
	DimensionRecommendation,
	ImageSelectionPlaceholder,
	FormattedText,
	Toolbar,
	Typography
} from "client/Components";
import {
	EMPTY_ARRAY,
	setTrackingId,
	getDataUrl
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useRef,
	useState
} from "react";
import styles from "./ImageSelect.module.less";
import { Base64FileUriUploadWithMetadataModel, fileMetadataSymbol } from "client/Misc/commonTypes";
import {
	AlertManager,
	DragOverEventArgs,
	DragOverEventResult,
	DropEventArgs,
	useAlerts,
	useDragAndDropEvents,
	useFileSelector,
	useIsTouchDevice,
	usePointerEventOutside
} from "client/Hooks";
import { FileSelectionEntry, SelectionEntry } from "client_legacy/FileUploader/utils";
import { partition } from "client_legacy/ndaw/arrayHelpers";
import classNames from "classnames";
import { useCompressImageBeforeUpload } from "../CompressImageDialog/useCompressImageBeforeUpload";

export type ImageSelectProps = {
	src: string | null;
	hasError: boolean;
	trackingId: string;
	maxImageSize: number | null | undefined;
	svgMaxSize?: number;
	supportedFormats: readonly string[];
	supportedFormatsText: readonly string[];
	dimensionRecommendation: DimensionRecommendation;
	onChange: (image: Base64FileUriUploadWithMetadataModel | null) => void;
	onEditFocalPoint?: () => void;
};

export const ImageSelect = (props: ImageSelectProps): ReactElement => {
	const {
		src,
		hasError,
		trackingId,
		maxImageSize,
		supportedFormats,
		svgMaxSize,
		supportedFormatsText,
		dimensionRecommendation,
		onChange,
		onEditFocalPoint
	} = props;

	const [isActive, setIsActive] = useState<boolean>(false);
	const alertManager: AlertManager = useAlerts();
	const isTouch: boolean = useIsTouchDevice();
	const previewRef: RefObject<HTMLDivElement | null> = useRef(null);

	const [isBusy, setIsBusy] = useState<boolean>(false);

	const [compressImageDialog, resolveFileForUpload] = useCompressImageBeforeUpload(
		maxImageSize ?? Number.POSITIVE_INFINITY,
		setIsBusy
	);

	const changeHandler = useCallback(
		async (selectedFile: File): Promise<void> => {
			const file: File = await resolveFileForUpload(selectedFile);
			const dataUrl: string = await getDataUrl(file);

			onChange({
				filename: file.name,
				uri: dataUrl,
				[fileMetadataSymbol]: {
					size: file.size,
					mimeType: file.type
				}
			});
		},
		[onChange, resolveFileForUpload]
	);

	const {
		open: openFileExplorer
	} = useFileSelector({
		isMultiple: false,
		contentTypes: supportedFormats,
		onChange: changeHandler
	});

	const dragOverHandler = useCallback(
		(args: DragOverEventArgs): DragOverEventResult => {
			const { types } = args;
			if (types.indexOf("Files") === -1) {
				return { isAccepted: false };
			}

			return {
				isAccepted: true,
				dropEffect: "copy"
			};
		},
		[]
	);

	const dropHandler = useCallback(
		async (args: DropEventArgs): Promise<void> => {
			const fileEntries: readonly SelectionEntry[] = await args.getFiles();

			const [directories, files] = partition(fileEntries, f => f.isDirectory);

			if (directories.length > 0) {
				alertManager.showError(localize("_YouCantUploadFolders"));
				return;
			}

			if (files.length === 0) {
				return;
			}

			const droppedFile: File = (files[0] as FileSelectionEntry).file;
			changeHandler(droppedFile);

		},
		[alertManager, changeHandler]
	);

	const {
		isDropTarget,
		...dragAndDropEventHandlers
	} = useDragAndDropEvents({
		onDragOver: dragOverHandler,
		onDrop: dropHandler
	});

	usePointerEventOutside(
		() => setIsActive(false),
		previewRef.current
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(src);
			setIsActive(false);
		},
		[src]
	);

	const renderContent = (): ReactElement => {
		if (src) {
			return (
				<div
					{...setTrackingId("preview")}
					className={classNames(
						styles.preview,
						{
							[styles.preview_hasError]: hasError
						}
					)}
					onClick={() => setIsActive(true)}
					ref={previewRef}
				>
					{hasError
						? (
							<ImageSelectionPlaceholder
								kind="error"
								trackingId="preview-error"
							/>
						)
						: (
							<img
								src={src}
							/>
						)
					}
					<div
						className={classNames(
							styles.previewActions,
							{ [styles.previewActions_show]: isActive && isTouch}
						)}
					>
						<Toolbar horizontalAlignment="center">
							<Toolbar.Button
								kind="secondary"
								text={localize("_Replace")}
								onClick={openFileExplorer}
								isDisabled={isBusy}
								trackingId="edit-image"
							/>

							{onEditFocalPoint && (
								<Toolbar.Button
									kind="secondary"
									text={localize("_EditFocalPoint")}
									onClick={() => onEditFocalPoint()}
									trackingId="edit-focal-point"
								/>
							)}

							<Toolbar.Button
								kind="secondary"
								text={localize("_Delete")}
								onClick={() => onChange(null)}
								trackingId="delete-image"
							/>
						</Toolbar>
					</div>
				</div>
			);
		}

		return (
			<ImageSelectionPlaceholder
				trackingId="placeholder"
				kind="default"
				title={(
					<FormattedText
						text={localize("_AddImageInfo")}
						thingsToReplace={EMPTY_ARRAY}
						thingsToWrap={
							[
								{
									blockId: "DragAndDrop",
									wrap: (children) => (
										<Typography weight="bold">
											{children}
										</Typography>
									)
								},
								{
									blockId: "Click",
									wrap: (children) => (
										<Typography weight="bold">
											{children}
										</Typography>
									)
								},
							]
						}
					/>
				)}
				dimensionRecommendation={dimensionRecommendation}
				maxFileSize={maxImageSize}
				supportedFormats={supportedFormatsText}
				svgMaxSize={svgMaxSize}
			/>
		);
	};

	return (
		<>
			<div
				{...setTrackingId(trackingId)}
				{...dragAndDropEventHandlers}
				className={classNames(
					styles.root,
					{
						[styles.root_isDragging]: isDropTarget
					}
				)}
				onClick={!src && !isBusy
					? openFileExplorer
					: undefined
				}
			>
				{renderContent()}
				{isBusy && <BusyIndicator />}
			</div>

			{compressImageDialog}
		</>
	);
};
ImageSelect.displayName = "ImageSelect";